import { useRef, useState, useCallback, useEffect } from 'react'
import { Box, Grid, Button, IconButton } from '@mui/material'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import { Edit, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import EditCompanyDialog from '@/components/EditCompanyDialog'
import CreateCompanyDialog from '@/components/CreateCompanyDialog'
import { connect, useSelector } from 'react-redux'
import PermissionsController from '@/components/PermissionsController'
import usePermissions from '@/hooks/usePermissions'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import get from 'lodash/get'
import ConfirmCompanyDialog from '@/shared/ConfirmCompanyDialog'
import Typography from '@mui/material/Typography'
import toUpper from 'lodash/toUpper'
import dayjs from 'dayjs'
import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'
import Preview from '@mui/icons-material/Preview'
import ViewPlanDialog from '@/components/ViewPlanDialog'


const mapStateToProps = ({ company, loading }) => ({
    companyList: company.companyList,
    isLoading: loading['company/getCompanyData'] || loading['plan/getPlanLevel'],
    deleteLoading: loading['company/deleteCompany'],
    isCompanyLoading: loading['company/deleteCompany'],
})

const fieldMap = {
    companyName: 'COMP_NAME',
    planName: 'PLAN_NAME',
    companyAccountCount: 'COMP_ACCOUNT_COUNT',
    status: 'STATUS'
}

const CompanyManagement = ({ dispatch, companyList, isLoading, deleteLoading }) => {

    const form = useRef()
    const { t } = useTranslation()
    const { data, paging = {} } = companyList || {}
    const [sort, setSort] = useState([{ field: 'companyName', sort: 'asc' }])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [deleteCompanyData, setDeleteCompanyData] = useState()
    const companyPageRef = useRef(0)
    const [companyResults, setCompanyResults] = useState({})
    const { data: companyResultsData = [] } = companyResults
    const [statusResults, setStatusResults] = useState({})
    const [planResults, setPlanResults] = useState([])
    const { data: statusResultsData = [] } = statusResults
    const [open, setOpen] = useState(false);
    const [editCompanyData, setEditCompanyData] = useState()
    const [viewPlanData, setViewPlanData] = useState()

    const selectedStatus = form.current?.getValues()?.status || null
    const accountId = useSelector(state => state.auth.account?.accountId)

    const canDelete = usePermissions('company_delete')
    const canModify = usePermissions('company_modify')


    const btnProps = {
        size: 'midle',
        sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
    }

    const iconProps = {
        sx: {
            padding: 0.3,
        },
    }

    const pagination = {
        ...paging,
        page,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
    }

    const cols = [
        { field: 'companyName', headerName: t('general.company_name'), width: 250 },
        {
            field: 'planName',
            headerName: t('general.plan'),
            width: 180,
        },
        {
            field: 'companyAccountCount',
            headerName: t('general.available_account_count_simple'),
            width: 120,
            valueGetter: (params) => {
                const compId = params?.row?.companyId
                const count = params?.row?.companyAccountCount

                if (compId === 1) return 'Max'   // 公司 ID = 1 顯示 Max
                return count != null ? count : 0 // 其他公司顯示實際數量，若 null 則顯示 0
            }
        },
        {
            field: 'period', headerName: t('general.authorization_duration'), width: 200,
            valueGetter: (params) => {
                const start = params?.row?.planStartTime
                const end = params?.row?.planEndTime
                const compId = params?.row?.companyId

                if (compId === 1) return t('general.permanent');

                if (!start || !end) return '-'

                return `${dayjs(start).format('YYYY/MM/DD')} ~ ${dayjs(end).format('YYYY/MM/DD')}`
            }
        },
        { field: 'status', headerName: t('general.authorization_status'), minWidth: 40, valueFormatter: ({ value }) => (value ? t(`status.${toUpper(value)}`) : '-'), },
        {
            field: 'view',
            hidden: !canModify,
            headerName: t('general.view'),
            minWidth: 200,
            renderCell: record =>
                record.row.editable === 1 && (
                    <IconButton
                        {...btnProps}
                        onClick={() => {
                            setViewPlanData(record.row)
                        }}
                    >
                        <Preview {...iconProps} />
                    </IconButton>
                ),
        },
        {
            field: 'edit',
            hidden: !canModify,
            headerName: t('general.edit'),
            minWidth: 10,
            renderCell: record =>
                record.row.editable === 1 && (
                    <IconButton
                        {...btnProps}
                        onClick={() => {
                            setEditCompanyData(record.row)
                        }}
                    >
                        <Edit />
                    </IconButton>
                ),
        },
        {
            field: 'delete',
            hidden: !canDelete,
            headerName: t('general.delete'),
            minWidth: 10,
            renderCell: record =>
                record.row.editable === 1 && (
                    <IconButton
                        {...btnProps}
                        onClick={() => {
                            setDeleteCompanyData(record.row)
                        }}
                    >
                        <Delete />
                    </IconButton>
                ),
        },
    ]

    const fetchData = useCallback(() => {
        const values = form.current?.getValues() || {}

        // 從 form 拿多欄位條件
        const payload = {
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            company: values?.company?.label || null,
            planName: values?.planName?.trim() || null,
            status: values?.status?.value || null,
        }

        dispatch({
            type: 'company/getCompanyData',
            payload,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, page, pageSize, form])

    const getCompany = params => {
        const { companyPage = 0, keyword = '' } = params || {}

        companyPageRef.current = companyPage

        dispatch({
            type: 'account/getCompany',
            payload: {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword, //  關鍵字
            },
            successCallback: response => {
                const { data: responseData = [], paging: responsePaging = {} } = response || {}
                const { pageIndex } = responsePaging

                const newData = responseData.map(d => ({
                    value: d.companyId,
                    label: d.companyName,
                }))

                setCompanyResults(s => ({
                    data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
                    paging: responsePaging,
                }))
            },
        })
    }

    const fetchCompanyData = useCallback(
        companyNameValue => {
            const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
            const payload = {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword: keyword || '', // 空值也可以發送
            }

            dispatch({
                type: 'account/getCompany',
                payload,
                successCallback: response => {
                    const { data: responseData = [], paging: responsePaging = {} } = response || {}
                    const { pageIndex } = responsePaging

                    const newData = responseData.map(d => ({
                        value: d.companyId,
                        label: d.companyName,
                    }))

                    setCompanyResults(s => ({
                        data: pageIndex > 0
                            ? unionBy(s.data || [], newData, 'value')
                            : newData,
                        paging: responsePaging,
                    }))
                },
            })
        },
        [dispatch],
    )

    const setCompanyNameDebounce = debounce((_, value) => {
        companyPageRef.current = 0
        fetchCompanyData(value)
    }, 400)

    const getPlanList = () => {
        dispatch({
            type: 'plan/getPlanLevel',
            successCallback: response => {
                const responseData = response?.data || []
                const planOptions = responseData.map(plan => ({
                    value: plan.id,
                    label: plan.name,
                }))
                setPlanResults(planOptions)
            },
        })
    }

    const getStatus = () => {
        const fixedStatus = [
            { value: 'A', label: t('status.ACTIVE') },
            { value: 'IA', label: t('status.INACTIVE') },
        ]

        setStatusResults({
            data: fixedStatus
        })
    }

    const deleteCompany = () => {
        if (deleteCompanyData) {
            dispatch({
                type: 'company/deleteCompany',
                payload: { companyId: deleteCompanyData.companyId },
                successCallback: () => {
                    dispatch({
                        type: 'app/notify',
                        payload: {
                            message: t('messages.company_deleted', { company: deleteCompanyData.companyName }),
                            severity: 'success',
                        },
                    })
                    setDeleteCompanyData()
                    fetchData()
                },
                errorCallback: () => {
                    dispatch({
                        type: 'app/notify',
                        payload: {
                            message: t('messages.company_delete_failed'),
                            severity: 'error',
                        },
                    });
                },
            })
        }
    }

    useEffect(() => {
        if (!accountId) return
        fetchData()
        getCompany()
        getPlanList()
        getStatus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, fetchData, page, pageSize]);


    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 520,
                    gap: 2,
                    border: '1px solid #2D2D6C',
                    borderRadius: 3,
                    overflow: 'hidden',
                    p: 2,
                    backgroundColor: '#fff',
                }}
            >
                {/* 搜尋區 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center',
                    }}
                >
                    <Grid item>
                        <FormCreator
                            ref={form}
                            formItems={[
                                {
                                    type: 'autocomplete',
                                    name: 'company',
                                    defaultValue: null,
                                    inputConfig: {
                                        options: companyResultsData,
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('company', selectedOption || null)
                                            form.current.setValue('companyName', selectedOption?.label || '')
                                            companyPageRef.current = 0
                                            fetchData()
                                        },
                                        onInputChange: (_, inputValue) => {
                                            setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                                        },
                                        placeholder: t('general.company_name'),
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'planName',
                                    inputConfig: {
                                        options: planResults,
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('planName', selectedOption?.label || '')

                                            fetchData()
                                        },
                                        placeholder: t('general.plan'),
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'status',
                                    inputConfig: {
                                        options: statusResultsData,
                                        value: selectedStatus, //  這裡指定目前選中值
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('status', selectedOption || null)
                                            form.current.setValue('statusName', selectedOption?.label || '')

                                            fetchData()
                                        },
                                        placeholder: t('general.authorization_status'),
                                    },
                                },
                            ]}
                            flexDirection="row"
                            submitComponent={() => null}
                            resetComponent={() => null}
                            gridItemProps={{ mr: 3 }}
                        />
                    </Grid>
                    <Box sx={{ flexGrow: 1 }} />
                    <PermissionsController permission="company_create">
                        <Grid item mr={2}>
                            <Button
                                variant="contained"
                                style={{ backgroundColor: '#2D2D6C', color: '#fff' }}
                                onClick={() => setOpen(true)}
                            >
                                + {t('general.add_logo')}
                            </Button>
                        </Grid>
                    </PermissionsController>
                </Box>

                {/* 表格區 */}
                <Table
                    minHeight={430}
                    columns={cols}
                    rows={data || []}
                    dataGridConfig={{
                        sortingMode: 'server',
                        sortingOrder: ['desc', 'asc'],
                        getRowId: row => row.companyId,
                        sortModel: sort,
                        onSortModelChange: newSort => setSort(newSort),
                        loading: isLoading,
                    }}
                    noRowsLabel={t('errors.no_test_companys')}
                    pagination={pagination}
                />
                {/* <Watermark username={username} /> */}
            </Box>

            <CreateCompanyDialog
                open={open}
                onClose={() => setOpen(false)}
                onComplete={() => {
                    fetchData()
                }}
            />

            <ViewPlanDialog
                open={!!viewPlanData}
                data={viewPlanData}
                onClose={() => {
                    setViewPlanData()
                }}
                onComplete={() => {
                    fetchData()
                }}
            />

            <EditCompanyDialog
                open={!!editCompanyData}
                data={editCompanyData}
                onClose={() => {
                    setEditCompanyData()
                }}
                onComplete={() => {
                    fetchData()
                }}
            />

            <ConfirmCompanyDialog
                title={t('messages.confirm_delete')}
                open={!!deleteCompanyData}
                onClose={() => {
                    setDeleteCompanyData()
                }}
                onSubmit={() => {
                    deleteCompany()
                }}
                loading={deleteLoading}
            >
                <Typography>
                    {t('messages.confirm_delete_company', { company: deleteCompanyData?.companyName })}
                </Typography>
            </ConfirmCompanyDialog>

        </>
    )
}

export default connect(mapStateToProps)(CompanyManagement)